import { Link } from "react-router-dom";
import { ArrowRight, Lock, Maximize2 } from "lucide-react";
import { useState } from "react";
import { resolveImage } from "@/lib/api";
import Lightbox from "./Lightbox";
import SpecialBadge from "./SpecialBadge";

export default function ArtworkCard({ art, index = 0 }) {
  const num = art.collection_number || (index + 1);
  const numStr = `#${String(num).padStart(2, "0")}`;
  const locked = !!art.locked;
  const [open, setOpen] = useState(false);

  return (
    <div className="feat-card group block" data-testid={`artwork-card-${art.slug}`}>
      {/* Image stage — shows the full artwork (no cropping) */}
      <div className="relative aspect-[4/5] bg-[#0a0a0a] overflow-hidden border border-white/10">
        <div className="num-badge">{numStr}</div>
        <SpecialBadge artwork={art} className="absolute bottom-3 left-3 z-[3]"/>
        {locked && (
          <div className="absolute top-3 left-3 z-[2] flex items-center gap-1.5 px-2.5 py-1 bg-black/70 backdrop-blur-md border border-[#C9A055]/60 text-[#C9A055] font-ui text-[9px] tracking-[0.28em]">
            <Lock size={10}/> Members
          </div>
        )}
        {/* Click to zoom (preview hint) */}
        {!locked && (
          <button
            type="button"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(true); }}
            className="absolute top-3 right-3 z-[2] w-9 h-9 bg-black/60 hover:bg-[#C9A055] hover:text-black border border-white/10 text-[#F4F1EA] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
            aria-label="Zoom image"
            data-testid={`artwork-zoom-${art.slug}`}
          >
            <Maximize2 size={14} />
          </button>
        )}
        <Link to={`/artwork/${art.slug}`} className="absolute inset-0 block">
          <img
            src={resolveImage(art.main_image)}
            alt={art.title}
            className={`absolute inset-0 w-full h-full object-contain p-3 md:p-4 transition-transform duration-700 group-hover:scale-[1.03] ${locked ? "blur-sm opacity-80" : ""}`}
            loading="lazy"
          />
          {/* Subtle vignette so the gold number badge stays legible */}
          <div className="absolute inset-0 pointer-events-none bg-gradient-to-b from-black/30 via-transparent to-black/20" />
        </Link>
      </div>

      {/* Title + CTA — BELOW the image so nothing obscures the artwork */}
      <Link to={`/artwork/${art.slug}`} className="block px-1 pt-4">
        <h3 className="font-display text-[#C9A055] text-xl md:text-2xl tracking-wide leading-tight mb-1.5">{(art.title || "").toUpperCase()}</h3>
        {art.short_description && (
          <p className="font-body text-[13px] text-[#B5B0A7] leading-snug mb-3 line-clamp-2">{art.short_description}</p>
        )}
        <div className="font-ui text-[10px] tracking-[0.32em] text-[#C9A055] flex items-center gap-2 group-hover:gap-3 transition-all">
          {locked ? <>Unlock with Membership <ArrowRight size={13} /></> : <>View Story <ArrowRight size={13} /></>}
        </div>
      </Link>

      {open && (
        <Lightbox
          images={[art.main_image, ...(art.additional_images || [])]}
          alt={art.title}
          onClose={() => setOpen(false)}
        />
      )}
    </div>
  );
}
